import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Menu from './Menu';
import { Tooltip } from '@mui/material';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField
} from '@mui/material';
import apiUrl from '../config';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box } from '@mui/material';
import '../CSS/Facture.css';
import LayoutDashboard from '../layout/LayoutDashboard';
import { DataGrid } from '@mui/x-data-grid';

const cellStyle = {
  color: '#1f6391',
  fontWeight: 'bold',
  backgroundColor: '#f0f0f0'
};

// ✅ Helper pour formater les montants en € (FR)
const formatCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '—';
  const num = Number(val);
  if (Number.isNaN(num)) return String(val);
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);
};



const FacturePage = () => {
  const navigate = useNavigate();
  const [factures, setFactures] = useState([]);
  const [filteredFactures, setFilteredFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [openDialog, setOpenDialog] = useState(false);
  const [factureToDelete, setFactureToDelete] = useState(null);
  const [filterStatus, setFilterStatus] = useState('Toutes');
  const [clientNames, setClientNames] = useState([]);
  const [filterClient, setFilterClient] = useState('Tous');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [openUnpayDialog, setOpenUnpayDialog] = useState(false);
  const [factureToToggle, setFactureToToggle] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
const [nomClient, setNomClient] = useState('');
const [collaborateur, setCollaborateur] = useState('');
const [dateFacture, setDateFacture] = useState('');
const [dateEcheance, setDateEcheance] = useState('');
const [numeroFacture, setNumeroFacture] = useState('');
const [montantHT, setMontantHT] = useState('');
const [montantTTC, setMontantTTC] = useState('');
const [statusFacture, setStatusFacture] = useState('Non payée');
const [fileFacture, setFileFacture] = useState(null);
const [loadingAddFacture, setLoadingAddFacture] = useState(false);

  const resetFilters = () => {
    setFilterStatus('Toutes');
    setFilterClient('Tous');
  };

  const reloadAndReset = async () => {
    await fetchFactures();  // recharge tout depuis le backend
    resetFilters();         // remet les filtres à zéro
  };

  const normalizeFull = (str) => {
    if (!str) return '';
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')  // enlever les accents
      .replace(/[^\w\s]/g, '')          // enlever ponctuation
      .replace(/\s+/g, ' ')             // espaces multiples -> un seul
      .trim()
      .toLowerCase()
      .split(' ')
      .sort()                           // trier les mots (ex: Youssef Harakat = Harakat Youssef)
      .join(' ');
  };

  const fetchFactures = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/facturebiam`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // ⚠️ Backend doit maintenant renvoyer montant_ht et montant_ttc
      setFactures(response.data);
      setFilteredFactures(response.data);

      // Liste des clients (normalisée pour fusionner "Nom Prénom" / "Prénom Nom")
      const uniqueClientsMap = new Map();
      response.data.forEach(f => {
        const original = f.nom_client;
        const normalized = normalizeFull(original);
        if (normalized && !uniqueClientsMap.has(normalized)) {
          uniqueClientsMap.set(normalized, original?.trim() ?? '');
        }
      });

      const uniqueClients = Array.from(uniqueClientsMap.entries()).sort((a, b) =>
        a[1].localeCompare(b[1], 'fr', { sensitivity: 'base' })
      );

      setClientNames(uniqueClients);
    } catch (error) {
      console.error('Erreur lors du chargement des factures:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFactures();
  }, []);

  useEffect(() => {
    let filtered = factures;

    if (filterStatus !== 'Toutes') {
      filtered = filtered.filter(f => f.status === filterStatus);
    }
    if (filterClient !== 'Tous') {
      filtered = filtered.filter(f => normalizeFull(f.nom_client) === filterClient);
    }
    setFilteredFactures(filtered);
  }, [filterStatus, filterClient, factures]);

  const updateStatus = async (factureId, newStatus) => {
  if (!factureId) return;
  setIsUpdatingStatus(true); // ✅ bloque le bouton
  try {
    const token = localStorage.getItem('token');
    await axios.put(
      `${apiUrl}/facturebiam/status/${factureId}`,
      { status: newStatus },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    await reloadAndReset();

    setNotification({
      open: true,
      message: newStatus === 'Payée' ? 'Facture marquée comme payée.' : 'Facture remise en "Non payée".',
      severity: 'success'
    });
  } catch (error) {
    setNotification({
      open: true,
      message: 'Erreur lors de la mise à jour de la facture.',
      severity: 'error'
    });
  } finally {
    setIsUpdatingStatus(false); // ✅ débloque
  }
};



  const handleDeleteFacture = async () => {
  if (!factureToDelete) return;
  setIsDeleting(true); // ✅ bloque le bouton
  try {
    const token = localStorage.getItem('token');
    await axios.delete(`${apiUrl}/facturebiam/${factureToDelete.id_facture}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    await reloadAndReset(); // recharge et reset filtres

    setNotification({
      open: true,
      message: 'Facture supprimée avec succès.',
      severity: 'success'
    });
  } catch (error) {
    console.error(error);
    setNotification({
      open: true,
      message: 'Erreur lors de la suppression de la facture.',
      severity: 'error'
    });
  } finally {
    setIsDeleting(false); // ✅ débloque le bouton
    setOpenDialog(false);
    setFactureToDelete(null);
  }
};




  const handleDownload = async (factureId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${apiUrl}/facturebiam/${factureId}/telecharger`, {
        headers: { Authorization: `Bearer ${token}` }, // NOTE: if this breaks, revert to { Authorization: `Bearer ${token}` }
        responseType: 'blob',
      });
      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = urlBlob;
      link.setAttribute('download', `facture_${factureId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Erreur lors du téléchargement de la facture:', error);
      setNotification({ open: true, message: 'Erreur lors du téléchargement de la facture', severity: 'error' });
    }
  };

  const handleAddFacture = async () => {

  if (
    !nomClient ||
    !dateFacture ||
    !dateEcheance ||
    !fileFacture ||
    !collaborateur ||
    !montantHT ||
    !montantTTC
  ) {
    setNotification({
      open: true,
      message: "Veuillez remplir tous les champs.",
      severity: "error"
    });

    return;
  }

  const formData = new FormData();

  formData.append("facture_pdf", fileFacture);
  formData.append("date_facture", dateFacture);
  formData.append("date_echeance", dateEcheance);
  formData.append("status", statusFacture);
  formData.append("nom_client", nomClient);
  formData.append("collaborateur", collaborateur);
  formData.append("montant_ht", montantHT);
  formData.append("montant_ttc", montantTTC);
  formData.append("numero_facture", numeroFacture);

  setLoadingAddFacture(true);

  try {
    const token = localStorage.getItem('token');

    await axios.post(`${apiUrl}/facturebiam/ajouter`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    setNotification({
      open: true,
      message: "Facture ajoutée avec succès.",
      severity: "success"
    });

    setOpenAddDialog(false);

    setNomClient('');
    setCollaborateur('');
    setDateFacture('');
    setDateEcheance('');
    setNumeroFacture('');
    setMontantHT('');
    setMontantTTC('');
    setStatusFacture('Non payée');
    setFileFacture(null);

    await reloadAndReset();

  } catch (error) {
    console.error("Erreur ajout facture:", error.response?.data || error);

    setNotification({
      open: true,
      message: error.response?.data?.error || error.response?.data?.message || "Erreur lors de l'ajout de la facture.",
      severity: "error"
    });

  } finally {
    setLoadingAddFacture(false);
  }
};

const rows = filteredFactures.map((f)=>({

id:f.id_facture,

...f

}));

const columns = [

{
field:"nom_client",
headerName:"Client",
flex:1,
minWidth:220,
renderCell:(params)=>(
<Tooltip title={params.value}>
<span>{params.value}</span>
</Tooltip>
)
},

{
field:"numero_facture",
headerName:"N° Facture",
flex:1,
minWidth:170
},

{
field:"date_facture",
headerName:"Date facture",
flex:1,
minWidth:170
},

{
field:"date_echeance",
headerName:"Date échéance",
flex:1,
minWidth:170
},

{
field:"status",
headerName:"Statut",
flex:1,
minWidth:150,
renderCell:(params)=>(

<Typography
sx={{
color: params.value==="Payée" ? "#16a34a" : "#dc2626",
fontWeight:"bold"
}}
>
{params.value}
</Typography>

)
},

{
field:"collaborateur",
headerName:"Collaborateur",
flex:1,
minWidth:220
},

{
field:"montant_ht",
headerName:"Montant HT",
flex:1,
minWidth:160,
valueFormatter:(params)=>formatCurrency(params.value)
},

{
field:"montant_ttc",
headerName:"Montant TTC",
flex:1,
minWidth:160,
valueFormatter:(params)=>formatCurrency(params.value)
},

{
field:"download",
headerName:"Télécharger",
minWidth:140,
sortable:false,
renderCell:(params)=>(

<DownloadIcon
sx={{cursor:"pointer"}}
onClick={()=>handleDownload(params.row.id_facture)}
/>

)
},

{
field:"statusAction",
headerName:"Paiement",
minWidth:120,
sortable:false,
renderCell:(params)=>(

<CheckCircleIcon
sx={{cursor:"pointer"}}
color={params.row.status==="Payée"?"success":"action"}

onClick={()=>{

if(params.row.status==="Payée"){

setFactureToToggle(params.row)
setOpenUnpayDialog(true)

}else{

updateStatus(params.row.id_facture,"Payée")

}

}}

>

</CheckCircleIcon>

)
},

{
field:"delete",
headerName:"Supprimer",
minWidth:120,
sortable:false,
renderCell:(params)=>(

<DeleteIcon
color="error"
sx={{cursor:"pointer"}}

onClick={()=>{

setFactureToDelete(params.row)
setOpenDialog(true)

}}

/>

)
}

];


 return (
<LayoutDashboard>

<div className="dashboard-page">

<div className="welcome-section">
<h2>Gestion des Factures</h2>
<p>
Consultez, téléchargez et gérez les factures des clients.
</p>
</div>

        <Container maxWidth="lg">
          <Button
variant="contained"
sx={{ mb:3, bgcolor:'#1f6391', color:'white' }}
onClick={() => setOpenAddDialog(true)}
>
+ Ajouter une facture
</Button>

          {/* Filtres */}
          <Box mb={2}>
            <Typography variant="body2" sx={{ color: '#0B349D', fontWeight: 550, mb: 0.5, textAlign: 'left' }}>
              Filtrer par statut
            </Typography>
            <FormControl fullWidth  >
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                displayEmpty
              >
                <MenuItem value="Toutes">Toutes</MenuItem>
                <MenuItem value="Payée">Payée</MenuItem>
                <MenuItem value="Non payée">Non payée</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box mb={2}>
            <Typography variant="body2" sx={{ color: '#0B349D', fontWeight: 550, mb: 0.5, textAlign: 'left' }}>
              Filtrer par client
            </Typography>
            <FormControl fullWidth  >
              <Select
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                displayEmpty
              >
                <MenuItem value="Tous">Tous</MenuItem>
                {clientNames.map(([key, original], index) => (
                  <MenuItem key={index} value={key}>{original}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Tableau */}
          <div className="facture-table-wrapper">

<Box
sx={{
width:"100%",
mt:3
}}
>
<DataGrid
rows={rows}
columns={columns}
autoHeight
pageSizeOptions={[5,10,20,50]}

initialState={{
pagination:{
paginationModel:{pageSize:10}
}
}}

disableRowSelectionOnClick

sx={{
width:"100%",
borderRadius:3,
background:"white",

"& .MuiDataGrid-columnHeaders":{
background:"#eef2f7",
fontWeight:"700",
fontSize:"15px",
color:"#1f6391",
borderBottom:"2px solid #e5e7eb"
},

"& .MuiDataGrid-cell":{
fontSize:"14px",
color:"#374151"
},

"& .MuiDataGrid-footerContainer":{
justifyContent:"space-between",
paddingLeft:"16px",
paddingRight:"16px"
},

"& .MuiDataGrid-columnHeaderTitle":{
whiteSpace:"normal"
}

}}
/>

</Box>

</div>
        </Container>

        {/* Dialog supprimer */}
        <Dialog open={openDialog} onClose={() => !isDeleting && setOpenDialog(false)}>
  <DialogTitle>Êtes-vous sûr ?</DialogTitle>
  <DialogContent sx={{ px:4, py:2 }}>
    <Typography>
      {isDeleting
        ? "Suppression en cours, veuillez patienter..."
        : "Voulez-vous vraiment supprimer cette facture ?"}
    </Typography>
  </DialogContent>
  <DialogActions>
    <Button
      onClick={() => setOpenDialog(false)}
      color="primary"
      disabled={isDeleting}
    >
      Annuler
    </Button>
    <Button
      onClick={handleDeleteFacture}
      color="error"
      disabled={isDeleting}
    >
      {isDeleting ? "Suppression..." : "Supprimer"}
    </Button>
  </DialogActions>
</Dialog>


        {/* Dialog remettre en "Non payée" */}
        <Dialog open={openUnpayDialog} onClose={() => !isUpdatingStatus && setOpenUnpayDialog(false)}>
  <DialogTitle>Confirmer</DialogTitle>
  <DialogContent sx={{ px:4, py:2 }}>
    <Typography>
      {isUpdatingStatus
        ? "Mise à jour en cours, veuillez patienter..."
        : 'Remettre cette facture en "Non payée" ?'}
    </Typography>
  </DialogContent>
  <DialogActions>
    <Button
      onClick={() => setOpenUnpayDialog(false)}
      color="primary"
      disabled={isUpdatingStatus}
    >
      Annuler
    </Button>
    <Button
      onClick={async () => {
        if (factureToToggle && !isUpdatingStatus) {
          await updateStatus(factureToToggle.id_facture, 'Non payée');
          setOpenUnpayDialog(false);
          setFactureToToggle(null);
        }
      }}
      color="error"
      disabled={isUpdatingStatus}
    >
      {isUpdatingStatus ? "Mise à jour..." : "Confirmer"}
    </Button>
  </DialogActions>
</Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={3000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setNotification({ ...notification, open: false })}
            severity={notification.severity}
            sx={{
              width: '100%',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </div>

     <Dialog
open={openAddDialog}
onClose={()=>setOpenAddDialog(false)}
fullWidth
maxWidth="md"
PaperProps={{
  sx:{
    border:"none",
    boxShadow:"0 4px 20px rgba(0,0,0,0.08)",
    borderRadius:"14px"
  }
}}
>

<DialogTitle sx={{fontWeight:"bold",color:"#1f6391",textAlign:"center"}}>
Ajouter une facture
</DialogTitle>

<DialogContent
sx={{
padding:3,
background:"transparent",
boxShadow:"none",
border:"none"
}}
>
<Box sx={{
display:"flex",
flexDirection:"column",
gap:2
}}>

<Typography sx={{fontWeight:600,color:"#1f6391",mb:0.5}}>
Numéro de facture
</Typography>

<TextField
variant="outlined"
value={numeroFacture}
onChange={(e)=>setNumeroFacture(e.target.value)}
fullWidth
size="small"
/>

<Typography sx={{fontWeight:600,color:"#1f6391",mb:0.5}}>
Nom du client
</Typography>

<TextField
variant="outlined"
value={nomClient}
onChange={(e)=>setNomClient(e.target.value)}
fullWidth
size="small"
/>

<Typography sx={{fontWeight:600,color:"#1f6391",mb:0.5}}>
Collaborateur
</Typography>

<TextField
variant="outlined"
value={collaborateur}
onChange={(e)=>setCollaborateur(e.target.value)}
fullWidth
size="small"
/>

<Typography sx={{fontWeight:600,color:"#1f6391",mb:0.5}}>
Montant HT
</Typography>

<TextField
variant="outlined"
type="number"
value={montantHT}
onChange={(e)=>setMontantHT(e.target.value)}
fullWidth
size="small"
/>

<Typography sx={{fontWeight:600,color:"#1f6391",mb:0.5}}>
Montant TTC
</Typography>

<TextField
variant="outlined"
type="number"
value={montantTTC}
onChange={(e)=>setMontantTTC(e.target.value)}
fullWidth
size="small"
/>

<Typography sx={{fontWeight:600,color:"#1f6391",mb:0.5}}>
Date facture
</Typography>

<TextField
variant="outlined"
type="date"
value={dateFacture}
onChange={(e)=>setDateFacture(e.target.value)}
fullWidth
size="small"
/>

<Typography sx={{fontWeight:600,color:"#1f6391",mb:0.5}}>
Date échéance
</Typography>

<TextField
variant="outlined"
type="date"
value={dateEcheance}
onChange={(e)=>setDateEcheance(e.target.value)}
fullWidth
size="small"
/>

<Box>

<Typography sx={{fontWeight:600,color:"#1f6391"}}>
Statut
</Typography>

<FormControl fullWidth size="small">

<Select
value={statusFacture}
onChange={(e)=>setStatusFacture(e.target.value)}
>

<MenuItem value="Non payée">Non payée</MenuItem>
<MenuItem value="Payée">Payée</MenuItem>

</Select>

</FormControl>

</Box>
<Button
  variant="outlined"
  component="label"
  sx={{ borderColor: "#1f6391", color: "#1f6391" }}
>
  Choisir fichier PDF

  <input
    type="file"
    hidden
    accept="application/pdf,.pdf"
    onChange={(e) => {
      const file = e.target.files?.[0] || null;
      setFileFacture(file);
    }}
  />
</Button>

{fileFacture && (
  <Typography sx={{ fontSize: "14px", color: "#16a34a", fontWeight: 600 }}>
    Fichier sélectionné : {fileFacture.name}
  </Typography>
)}

</Box>

</DialogContent>

<DialogActions sx={{justifyContent:"center",pb:3, gap:2}}>

<Button
variant="outlined"
onClick={()=>setOpenAddDialog(false)}
sx={{borderColor:"#1f6391",color:"#1f6391"}}
>
Annuler
</Button>

<Button
variant="contained"
onClick={handleAddFacture}
sx={{bgcolor:"#1f6391"}}
disabled={loadingAddFacture}
>

{loadingAddFacture ? "Merci de patienter..." : "Ajouter la facture"}

</Button>

</DialogActions>

</Dialog>
    </LayoutDashboard>
  );
};

export default FacturePage;
